import { AxesHelper, Bone, Box3, BoxGeometry, Camera, CameraHelper, Controls, DirectionalLight, DirectionalLightHelper, Intersection, IUniform, Light, Loader, LoadingManager, Material, Matrix4, Mesh, MeshBasicMaterial, Object3D, PointLight, PointLightHelper, Quaternion, Raycaster, Scene, SkeletonHelper, SkinnedMesh, SphereGeometry, SpotLightHelper, Texture, TextureLoader, UniformsGroup, UniformsLib, Vector2, Vector3, WebGLRenderer } from "three";
import { ILogService } from "../../log/common/log";
import { NodeFlags } from "../../hierarchy/common/hierarchy";
import { isHideInHierarchy } from "../../hierarchy/common/hierarchyService";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module";
import { GLTFLike } from "../common/gltf";
import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader.js";
import { URI } from "../../../base/common/uri";
import { CancellationToken } from "../../../base/common/cancellation";
import { isColorLike } from "../../properties/common/types";
import { Context, getLoader, registerComponentExtension } from "@needle-tools/engine";


// #region Three.js Version

// Extract Three.js version number from build-time constant
// __THREE_VERSION__ is injected by Vite at build time via define
// Note: Three.js uses 0.x.x versioning, so we use the minor version (second number)
// e.g., "0.169.10" → 169, "0.182.0" → 182
export const THREE_MAJOR_VERSION: number = (() => {
    try {
        // @ts-ignore - __THREE_VERSION__ is defined by Vite
        const version = __THREE_VERSION__;
        if (version && typeof version === 'string') {
            const parts = version.split('.');
            // Three.js is 0.x.x, so use the minor version (index 1)
            const minorVersion = parseInt(parts[1]);
            return isNaN(minorVersion) ? -1 : minorVersion;
        }
    } catch (e) {
        // __THREE_VERSION__ not defined - fallback to -1
    }
    return -1;
})();


// #region Type Guards

export function isRenderer(obj: unknown): obj is (import("three").WebGLRenderer & (
    { readonly isWebGLRenderer: true, readonly isWebGPURenderer: undefined, } |
    { readonly isWebGLRenderer: undefined, readonly isWebGPURenderer: true }
)) {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const renderer = obj as import("three").WebGLRenderer;
    return typeof renderer.domElement === "object" && typeof renderer.render === "function";
}

// https://github.com/mrdoob/three.js/blob/3ef307076213aa05265d522dbc05c56e2a870a41/src/renderers/common/Renderer.js#L109
type RendererWithBackend = {
    isRenderer: true,
    backend: {
        data: WeakMap<object, unknown>
    }
};
export function isRendererWithBackend(obj: unknown): obj is RendererWithBackend {
    if (!obj) return false;
    const renderer = obj as RendererWithBackend;
    return renderer.isRenderer === true && renderer.backend !== null && typeof renderer.backend === "object" && renderer.backend.data instanceof WeakMap;
}
export function isWebGLRenderer(obj: unknown): obj is import("three").WebGLRenderer {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const renderer = obj as import("three").WebGLRenderer;
    return (renderer as any).isWebGLRenderer === true && typeof renderer.domElement === "object" && typeof renderer.render === "function";
}
export function isWebGPURenderer(obj: unknown): obj is { isWebGPURenderer: true } & RendererWithBackend {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const renderer = obj as { isWebGPURenderer: true };
    return (renderer as any).isWebGPURenderer === true;
}

export function isScene(obj: unknown): obj is import("three").Scene {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const scene = obj as import("three").Scene;
    return scene.isScene === true && typeof scene.uuid === "string";
}
export function isCamera(obj: unknown): obj is Camera {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const camera = obj as Camera;
    return camera.isCamera === true && typeof camera.uuid === "string";
}
export function isPerspectiveCamera(obj: unknown): obj is import("three").PerspectiveCamera {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const camera = obj as import("three").PerspectiveCamera;
    return camera.isPerspectiveCamera === true && typeof camera.uuid === "string";
}
export function isObject3D(obj: unknown): obj is Object3D {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const obj3d = obj as Object3D;
    return obj3d.isObject3D === true && typeof obj3d.uuid === "string";
}
export function isMeshLike(obj: unknown): obj is Mesh {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mesh = obj as Mesh;
    return mesh.isMesh === true && typeof mesh.uuid === "string";
}
export function isSkinnedMeshLike(obj: unknown): obj is SkinnedMesh {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mesh = obj as SkinnedMesh;
    return mesh.isSkinnedMesh === true && typeof mesh.uuid === "string";
}
export function isSkeleton(obj: unknown): obj is import("three").Skeleton {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const skeleton = obj as import("three").Skeleton;
    return typeof skeleton.bones === "object" && Array.isArray(skeleton.bones);
}
export function isBone(obj: unknown): obj is Bone {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const bone = obj as Bone;
    return bone.isBone === true && typeof bone.uuid === "string";
}
export function isInstancedMeshLike(obj: unknown): obj is import("three").InstancedMesh {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mesh = obj as import("three").InstancedMesh;
    return mesh.isInstancedMesh === true && typeof mesh.uuid === "string";
}
export function isBatchedMeshlike(obj: unknown): obj is import("three").BatchedMesh {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mesh = obj as import("three").BatchedMesh;
    return mesh.isBatchedMesh === true && typeof mesh.uuid === "string";
}
export function isGeometry(obj: unknown): obj is import("three").BufferGeometry {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const geom = obj as import("three").BufferGeometry;
    return geom.isBufferGeometry === true && typeof geom.uuid === "string";
}
export function isMaterial(obj: unknown): obj is Material {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mat = obj as Material;
    return mat.isMaterial === true && typeof mat.uuid === "string";
}
export function isShaderMaterial(obj: unknown): obj is import("three").ShaderMaterial {
    return isMaterial(obj) && (obj as import("three").ShaderMaterial).isShaderMaterial === true;
}
export function isRawShaderMaterial(obj: unknown): obj is import("three").RawShaderMaterial {
    return isMaterial(obj) && (obj as import("three").RawShaderMaterial).isRawShaderMaterial === true;
}
export function isUniformsGroup(obj: unknown): obj is UniformsGroup {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const ug = obj as UniformsGroup;
    return ug.isUniformsGroup === true && typeof ug.id === "string";
}
export function isUniformsArray(obj: unknown): obj is Array<import("three").IUniform> {
    if (!obj) return false;
    if (!Array.isArray(obj)) return false;
    const arr = obj as Array<import("three").IUniform>;
    for (const item of arr) {
        if (typeof item !== "object" || !("value" in item)) {
            return false;
        }
    }
    return true;
}


const commonUniformsTextureSlotNames = new Set<string>([
    "map",
    "alphaMap",
    "aoMap",
    "bumpMap",
    "displacementMap",
    "emissiveMap",
    "envMap",
    "lightMap",
    "metalnessMap",
    "normalMap",
    "roughnessMap",
    "specularMap",
    "gradientMap",
    "matcap",
    "lightProbeMap",
    "gradientMap",
]);
type MaterialTextureSlotOwner = Material | UniformsGroup | Array<IUniform>;
// owner: MaterialTextureSlotOwner, 
export function isLikelyMaterialTextureInputSlot(slotName: string, slotValue?: unknown): boolean | undefined {
    if (slotValue && isTexture(slotValue)) {
        return true;
    }
    if (commonUniformsTextureSlotNames.has(slotName)) {
        return true;
    }
    if (slotName.endsWith("Map")) {
        return true;
    }
    else if (slotName.startsWith("u") && (slotName.endsWith("Map") || slotName.endsWith("Tex"))) {
        return true;
    }
    return undefined;
}

export function isLikelyColorInputSlot(slotName: string, slotValue?: unknown): boolean | undefined {
    if (slotValue && isColorLike(slotValue) === true) {
        return true;
    }
    else if (slotName.endsWith("Color") ||
        slotName.endsWith("Colour") ||
        slotName.toLowerCase() === "color" ||
        slotName.toLowerCase() === "colour"
    ) {
        return true;
    }
    return undefined;
}


// #region Geometry Helper

export function isInterleavedBufferAttribute(obj: unknown): obj is import("three").InterleavedBufferAttribute {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const attr = obj as import("three").InterleavedBufferAttribute;
    return attr.isInterleavedBufferAttribute === true;
}

/*
export const StaticDrawUsage: 35044;
export const DynamicDrawUsage: 35048;
export const StreamDrawUsage: 35040;
export const StaticReadUsage: 35045;
export const DynamicReadUsage: 35049;
export const StreamReadUsage: 35041;
export const StaticCopyUsage: 35046;
export const DynamicCopyUsage: 35050;
export const StreamCopyUsage: 35042;
*/
export function geometryAttributeUsageToString(usage: number): string | undefined {
    switch (usage) {
        case 35044: return "StaticDrawUsage";
        case 35048: return "DynamicDrawUsage";
        case 35040: return "StreamDrawUsage";
        case 35045: return "StaticReadUsage";
        case 35049: return "DynamicReadUsage";
        case 35041: return "StreamReadUsage";
        case 35046: return "StaticCopyUsage";
        case 35050: return "DynamicCopyUsage";
        case 35042: return "StreamCopyUsage";
        default: return undefined;
    }
    return undefined;
}

/*
export const UnsignedByteType: 1009;
export const ByteType: 1010;
export const ShortType: 1011;
export const UnsignedShortType: 1012;
export const IntType: 1013;
export const UnsignedIntType: 1014;
export const FloatType: 1015;
export const HalfFloatType: 1016;
export const UnsignedShort4444Type: 1017;
export const UnsignedShort5551Type: 1018;
export const UnsignedInt248Type: 1020;
export const UnsignedInt5999Type: 35902;
export const UnsignedInt101111Type: 35899;

export type AttributeGPUType = typeof FloatType | typeof IntType;
*/

export function geometryAttributeGpuTypeToString(gpuType: number | undefined): string | undefined {
    switch (gpuType) {
        case 1009: return "UnsignedByteType";
        case 1010: return "ByteType";
        case 1011: return "ShortType";
        case 1012: return "UnsignedShortType";
        case 1013: return "IntType";
        case 1014: return "UnsignedIntType";
        case 1015: return "FloatType";
        case 1016: return "HalfFloatType";
        case 1017: return "UnsignedShort4444Type";
        case 1018: return "UnsignedShort5551Type";
        case 1020: return "UnsignedInt248Type";
        case 35902: return "UnsignedInt5999Type";
        case 35899: return "UnsignedInt101111Type";
        default: return undefined;
    }
}


// #region Texture Types
export function isTexture(obj: unknown): obj is import("three").Texture {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const tex = obj as import("three").Texture;
    return tex.isTexture === true && typeof tex.uuid === "string";
}
export function isCompressedTexture(obj: unknown): obj is import("three").CompressedTexture {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const tex = obj as import("three").CompressedTexture;
    return tex.isCompressedTexture === true && typeof tex.uuid === "string";
}
export function isDataTexture(obj: unknown): obj is import("three").DataTexture {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const tex = obj as import("three").DataTexture;
    return tex.isDataTexture === true && typeof tex.uuid === "string";
}
export function isCubeTexture(obj: unknown): obj is import("three").CubeTexture {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const tex = obj as import("three").CubeTexture;
    return tex.isCubeTexture === true && typeof tex.uuid === "string";
}
export function isWebGLRenderTarget(obj: unknown): obj is import("three").WebGLRenderTarget {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const rt = obj as import("three").WebGLRenderTarget;
    return rt.isWebGLRenderTarget === true && typeof rt.texture.uuid === "string";
}


export function isLight(obj: unknown): obj is Light {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const light = obj as Light;
    return light.isLight === true && typeof light.uuid === "string";
}
export function isLayermask(obj: unknown): obj is import("three").Layers {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const layers = obj as import("three").Layers;
    return "mask" in layers && typeof layers.mask === "number" && typeof layers.set === "function" && typeof layers.enable === "function";
}
export function isLineObject(obj: unknown): obj is import("three").Line {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const line = obj as import("three").Line;
    return line.isLine === true && typeof line.uuid === "string";
}
export function isTransformControls(obj: unknown): obj is import("three/examples/jsm/controls/TransformControls").TransformControls {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const controls = obj as import("three/examples/jsm/controls/TransformControls").TransformControls;
    if ("axis" in controls && "mode" in controls && "space" in controls) {
        return true;
    }
    return false;
}

export type ObjectWithMaterial = Object3D & { material: Material | Material[] };

export function isObjectWithMaterials(object: Object3D): object is ObjectWithMaterial {
    return !!(object as any).material;
}

export function safeClone(obj: ObjectWithMaterial, logger: ILogService): ObjectWithMaterial | null {

    let clone: ObjectWithMaterial | null = null;

    try {
        switch (obj.type) {
            case "Mesh":
                // case "SkinnedMesh":
                const mesh = obj as Mesh | SkinnedMesh;

                // make sure the object has a valid geometry assignment
                if (!mesh.geometry || !isGeometry(mesh.geometry)) {
                    // Is this a Spline object? 
                    if ('geometryCreateDeleyed' in mesh) {
                        if (typeof mesh.geometryCreateDeleyed === "function") {
                            mesh.geometryCreateDeleyed();
                        }
                        else {
                            mesh.geometry = mesh.geometryCreateDeleyed as any;
                        }
                    }
                    if (!isGeometry(mesh.geometry)) {
                        logger.warn(`Mesh ${mesh.name || "unnamed"} has no geometry assigned, cannot clone properly.`);
                        return null;
                    }
                }

                clone = new (Object.getPrototypeOf(obj).constructor)(mesh.geometry, mesh.material);
                const clonedMesh = clone as Mesh;
                clonedMesh.geometry = mesh.geometry;
                clonedMesh.material = mesh.material;
                break;

            // case "SkinnedMesh":
            //     const skinnedMesh = obj as SkinnedMesh;
            //     clone = new (Object.getPrototypeOf(obj).constructor)(skinnedMesh.geometry, skinnedMesh.material);
            //     break;

            default:
                try {
                    return obj.clone() as ObjectWithMaterial;
                }
                catch (error) {
                    logger.debug(`Object3D.clone() failed for type ${obj.type} (${obj.name || "unnamed"})`, error);
                    return null;
                }
        }
    }
    catch (error) {
        logger.debug(`Could not clone object of type ${obj.type} (${obj.name || "unnamed"})`, error);
        return null;
    }

    if (!clone) {
        throw new Error(`Could not clone object of type ${obj.type}`);
    }

    clone.name = obj.name;
    clone.position.copy(obj.position);
    clone.quaternion.copy(obj.quaternion);
    clone.scale.copy(obj.scale);
    clone.matrix.copy(obj.matrix);
    clone.matrixWorld.copy(obj.matrixWorld);
    clone.layers.mask = obj.layers.mask;
    clone.uuid = Date.now().toString() + Math.random().toString();
    clone.renderOrder = obj.renderOrder + 1;

    return clone;
}



export function createHelperObjectIfNecessary(obj: Object3D, options: {
    camera?: Camera | null,
    htmlElement?: HTMLElement | null,
    allowAxesHelper?: boolean

}): Array<Object3D | Controls<any>> | null {
    if (isLight(obj)) {
        switch (obj.type) {
            case "DirectionalLight":
                {
                    const light = obj as DirectionalLight;
                    const helper = new DirectionalLightHelper(light, 1);
                    return [helper];
                }
            case "PointLight":
                {
                    const light = obj as PointLight;
                    const helper = new PointLightHelper(light, light.distance || 1);
                    return [helper];
                }
            case "SpotLight":
                {
                    const helper = new SpotLightHelper(obj as Light);
                    return [helper];
                }
        }
    }
    else if (isCamera(obj)) {
        if (obj.coordinateSystem === undefined) {
            console.warn("Camera has no coordinate system defined, cannot create helper.");
            return null;
        }
        // TODO: somehow the camera helper breaks in chrome extension (sometimes?) e.g. here https://engine.needle.tools/samples-uploads/physics-animation/
        const helper = new CameraHelper(obj as Camera);
        /** @ts-expect-error */
        helper.update = undefined;
        return [helper];
    }
    else if (isSkinnedMeshLike(obj)) {
        const skinnedMesh = obj as SkinnedMesh;
        const skeletonHelper = new SkeletonHelper(skinnedMesh);
        return [skeletonHelper];
    }


    // TODO: importing TransformControls here causes issues with three.js builds that do not include it
    // if (options.camera && options.htmlElement) {
    //     const transformControls = new TransformControls(options.camera, options.htmlElement);
    //     transformControls.attach(obj);
    //     transformControls.setSize(1.2);
    //     return [transformControls];
    // }

    return null;
}


// #region Raycast


const raycasterVec2 = new Vector2();
export function getRaycaster(evt: PointerEvent | MouseEvent | { x: number, y: number }, camera: Camera, htmlElement: HTMLElement): Raycaster {
    const rect = htmlElement.getBoundingClientRect();
    let x = 0;
    let y = 0;
    if ('clientX' in evt && 'clientY' in evt) {
        x = evt.clientX;
        y = evt.clientY;
    }
    else {
        x = evt.x;
        y = evt.y;
    }

    const nx = ((x - rect.left) / rect.width) * 2 - 1;
    const ny = -((y - rect.top) / rect.height) * 2 + 1;
    const rc = new Raycaster();
    raycasterVec2.set(nx, ny);
    rc.setFromCamera(raycasterVec2, camera);
    rc.layers = camera.layers;
    if (isPerspectiveCamera(camera)) {
        rc.near = camera.near;
        rc.far = camera.far;
    } else {
        rc.near = 0;
        rc.far = 100_000;
    }
    return rc;
}

const _raycastSeenObject = new Set<object>();

// export function raycast(clientX:number, clientY:numbr)

export function raycast(raycaster: Raycaster, objects: Object3D[], results: Intersection[] = [], sort?: boolean): Intersection[] {

    results.length = 0;
    if (!objects || objects.length === 0) {
        return results;
    }

    for (const obj of objects) {
        _recursiveRaycast(obj, results, _raycastSeenObject);
    }

    _raycastSeenObject.clear();
    if (sort) {
        results.sort((a, b) => a.distance - b.distance);
    }
    return results;


    function _recursiveRaycast(obj: Object3D, results: Intersection[], seen: Set<object>): void {
        if (!obj) return;
        if (!obj.visible) return;
        if (isHideInHierarchy(obj)) return;

        if (seen.has(obj)) return;
        seen.add(obj);

        // if (isBone(obj)) {
        //     results = raycastBone(obj, raycaster, results);
        // }

        if ("geometry" in obj && obj.geometry) {
            results = raycaster.intersectObject(obj, false, results);
        }

        for (const child of obj.children) {
            _recursiveRaycast(child, results, seen);
        }
    }
}


const boneMesh = new Mesh(
    new BoxGeometry(1, 1, 1),
    new MeshBasicMaterial({ visible: false })
);


// function raycastBone(bone: Bone, raycaster: Raycaster, target: Intersection[]): Intersection[] {

//     bone.updateWorldMatrix(true, false);
//     boneMesh.position.setFromMatrixPosition(bone.matrixWorld);
//     boneMesh.scale.set(0.1, 0.1, 0.1);
//     boneMesh.updateMatrixWorld(true);

//     console.log("RAYCAST BONE", bone.name, boneMesh.position);
//     return raycaster.intersectObject(boneMesh, false, target);
// }

// #region Fit Camera

export function fitCamera(camera: Camera, object: Object3D[]): null | { newPosition: Vector3, newRotation: Quaternion, fov?: number, near?: number, far?: number } {

    if (!isPerspectiveCamera(camera)) {
        return null;
    }


    const box = new Box3();
    for (const obj of object) {
        obj.traverse((child) => {
            box.expandByObject(child);
        });
    }
    const size = box.getSize(new Vector3());
    const center = box.getCenter(new Vector3());
    const maxSize = Math.max(size.x, size.y, size.z);
    const fitHeightDistance = maxSize / (2 * Math.tan(Math.PI * camera.fov / 360));
    const fitWidthDistance = fitHeightDistance / camera.aspect;
    const distance = Math.max(fitHeightDistance, fitWidthDistance) * 1.3;

    const worldPosition = new Vector3();
    camera.getWorldPosition(worldPosition);

    const direction = new Vector3();
    direction.subVectors(worldPosition, center).normalize();

    // update look at but keep camera up vector
    const newCameraLookRotation = new Quaternion();
    const m1 = new Vector3();
    m1.addVectors(center, direction);
    const up = new Vector3(0, 1, 0);
    newCameraLookRotation.setFromRotationMatrix(
        new Matrix4().lookAt(
            m1,
            center,
            up
        )
    );

    const position = direction.multiplyScalar(distance).add(center);
    return {
        newPosition: position,
        newRotation: newCameraLookRotation,
    };
}


// #region Blink
interface BlinkOptions {
    repeat?: number;
    intervalMs?: number;
}
export async function blink(object: Object3D, options?: BlinkOptions, logger?: ILogService): Promise<void> {

    const repeat = options?.repeat || 3;
    const intervalMs = options?.intervalMs || 200;
    const startVisible = object.visible;
    for (let i = 0; i < repeat; i++) {
        try {
            object.visible = !startVisible;
            await new Promise<void>((resolve) => setTimeout(() => resolve(), intervalMs));
            object.visible = startVisible;
            await new Promise<void>((resolve) => setTimeout(() => resolve(), intervalMs));
        }
        catch (err) {
            logger?.error("Error during blink operation", err);
        }
    }
    object.visible = startVisible;
}


// #region GLTFLoader
export let ktx2Loader: KTX2Loader | undefined;
try {
    ktx2Loader = new KTX2Loader();
    ktx2Loader.detectSupport(new WebGLRenderer());
    ktx2Loader.setTranscoderPath('https://cdn.needle.tools/static/three/0.179.1/basis2/');
} catch { /* non-fatal: agent/headless environments stub THREE */ }

export let dracoLoader: DRACOLoader | undefined;
try {
    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
} catch { /* non-fatal: agent/headless environments stub THREE */ }

type LoadGLTFOptions = {
    resourcePath?: string,
    resolveUrl?: LoadingManager["resolveURL"]
}

export async function loadGLTF(data: ArrayBuffer | URI, token?: CancellationToken, options?: LoadGLTFOptions): Promise<GLTFLike<Scene> | null> {

    let resourcePath = options?.resourcePath || undefined;

    const gltfLoader = new GLTFLoader();
    gltfLoader.resourcePath = options?.resourcePath || resourcePath || '';
    if (dracoLoader) gltfLoader.setDRACOLoader(dracoLoader);
    if (ktx2Loader) gltfLoader.setKTX2Loader(ktx2Loader);
    gltfLoader.setMeshoptDecoder(MeshoptDecoder);

    if (options?.resolveUrl) gltfLoader.manager.resolveURL = options.resolveUrl;

    // gltfLoader.register(_parser => {
    //     return {
    //         name: 'NeedleEngineExtension',
    //         async afterRoot(gltf: GLTF) {
    //             console.log("LOADED GLTF", gltf);
    //         }
    //     }
    // })

    // let baseUrl = '';
    // if (data instanceof URI) {
    //     baseUrl = data.toString().substring(0, data.toString().lastIndexOf('/') + 1);
    // }
    // else options?.resolveUrl = url => {
    //     if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
    //         return url;
    //     }
    //     return baseUrl + url;
    // }

    try {
        const gltf = data instanceof URI
            ? await gltfLoader.loadAsync(data.toString())
            : await gltfLoader.parseAsync(data, '');

        // if (ext?.gltf)
        //     getLoader().createBuiltinComponents(Context.Current, "", gltf, null, ext);


        return gltf as unknown as GLTFLike<Scene>;

    } catch (error) {
        console.error("Failed to load GLTF:", error);
        return null;
    }
}


export async function loadTexture(_url: string | URI, type?: "hdr" | "exr" | "ktx2" | "texture"): Promise<Texture | null> {
    let loader: Loader<Texture | null> | null = null;

    const url = typeof _url === "string" ? _url : _url.toString();

    if (!type) {
        const urlLower = url.toLowerCase();
        if (urlLower.endsWith(".hdr")) {
            type = "hdr";
        }
        else if (urlLower.endsWith(".exr")) {
            type = "exr";
        }
        else if (urlLower.endsWith(".ktx2")) {
            type = "ktx2";
        }
        else {
            type = "texture";
        }
    }

    if (type === "hdr") {
        await import("three/examples/jsm/loaders/RGBELoader.js").then(({ RGBELoader }) => {
            loader = new RGBELoader();
        });
        // loader = new HDRLoader()
    }
    else if (type === "exr") {
        loader = new EXRLoader();
    }
    else if (type === "ktx2") {
        const ktx2Loader = new KTX2Loader();
        ktx2Loader.setTranscoderPath('https://cdn.needle.tools/static/three/0.179.1/basis2/');
        loader = ktx2Loader;
    }
    else if (type === "texture") {
        loader = new TextureLoader();
    }

    if (!loader) {
        console.warn(`No loader available for texture type: ${type}`);
        return null;
    }

    return loader.loadAsync(url);
}